import DataLoader from 'dataloader'
import { type DocumentsApi } from '@/features/documents/api/documents-api'
import { collection } from '@/infra/collection'
import { Id } from '@/infra/id'
import { DocumentType, type DocumentTypeProps } from './DocumentType'
import { mapDocumentTypeResourceToProps } from './document-mappers'

/**
 * Props for priming a document type whose ID is known.
 */
export interface PrimeDocumentTypeProps extends Partial<DocumentTypeProps> {
  id: Id
}

/**
 * Manages document types.
 */
export class DocumentTypeStore {
  private readonly _loader = new DataLoader(this._fetchTypesCore.bind(this))

  readonly types = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new DocumentType(),
  })

  constructor(private readonly _api: DocumentsApi) {}

  /**
   * Primes a document type in the collection.
   */
  primeDocumentType(props: PrimeDocumentTypeProps) {
    return this.types.set(props)
  }

  /**
   * Primes a document type by its short ID and refreshes it in the
   * background.
   */
  primeDocumentTypeByShortId(
    shortId?: string,
    name?: string,
  ): DocumentType | undefined {
    if (!shortId) {
      return undefined
    }

    const existing = this.types.get(shortId)
    const type = existing ?? this.types.create({ id: shortId, name })

    if (existing && name && !existing.name) {
      existing.set({ name })
    }

    this.fetchDocumentType(shortId).catch(() => undefined)

    return type
  }

  /**
   * Fetches a document type by its short ID.
   */
  async fetchDocumentType(shortId: Id): Promise<DocumentType | null> {
    return (await this._loader.load(Id.assert(shortId))) ?? null
  }

  /**
   * Lists the document types.
   */
  async listTypes(signal?: AbortSignal): Promise<DocumentType[]> {
    const response = await this._api.types.list(signal)
    const types = this.types.set(response.map(mapDocumentTypeResourceToProps))

    types.forEach((type) => this._loader.prime(Id.assert(type.id), type))

    return types
  }

  /**
   * Fetches document types by their short IDs.
   */
  private async _fetchTypesCore(ids: readonly string[]) {
    const resources = await this._api.types.getMany({ ids })
    return this.types.set(
      resources.map((r) => (r ? mapDocumentTypeResourceToProps(r) : null)),
    )
  }
}
